import { Button, StatusBadge } from '@aecom-ai-engineering/react-components';
import { Drawer } from '@ark-ui/react/drawer';
import { Portal } from '@ark-ui/react/portal';
import clsx from 'clsx';
import { ChevronLeft, Maximize2, Minimize2, X } from 'lucide-react';
import {
  type FC,
  type KeyboardEventHandler,
  type PointerEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { AgentChatLogo } from '@/components/agent-chat-logo';
import { prefersReducedMotion } from '@/util/prefers-reduced-motion';

import { PropChatContent } from './prop-chat-content';
import './agent-chat-drawer.css';

interface AgentChatDrawerProps {
  onClose: () => void;
  onEntered?: () => void;
  onMaximizedChange?: (isMaximized: boolean) => void;
  onResizingChange: (isResizing: boolean) => void;
  onWidthChange: (width: number) => void;
  width: number;
}

const MIN_DRAWER_WIDTH = 360;
const MAX_DRAWER_WIDTH = 720;
const MIN_PAGE_WIDTH = 320;
const KEYBOARD_RESIZE_STEP = 16;
const DRAWER_TRANSITION_MS = 300;
const DRAWER_TRANSITION_FALLBACK_MS = DRAWER_TRANSITION_MS + 50;

const getMaximumDrawerWidth = () => {
  if (typeof window === 'undefined') return MAX_DRAWER_WIDTH;

  return Math.max(MIN_DRAWER_WIDTH, Math.min(MAX_DRAWER_WIDTH, window.innerWidth - MIN_PAGE_WIDTH));
};

const clampDrawerWidth = (width: number) =>
  Math.min(getMaximumDrawerWidth(), Math.max(MIN_DRAWER_WIDTH, width));

export const AgentChatDrawer: FC<AgentChatDrawerProps> = ({
  onClose,
  onEntered,
  onMaximizedChange,
  onResizingChange,
  onWidthChange,
  width,
}) => {
  const { t } = useTranslation();
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFullscreenContent, setIsFullscreenContent] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartRef = useRef<{ pointerX: number; width: number } | null>(null);
  const minimizeTimeoutRef = useRef<number | null>(null);
  const entryTimeoutRef = useRef<number | null>(null);
  const hasEnteredRef = useRef(false);
  const isRestoringRef = useRef(false);
  const resizeLabel = t(isFullscreenContent ? 'prop-chat.restore' : 'prop-chat.maximize');
  const finishEntering = useCallback(() => {
    if (hasEnteredRef.current) return;
    hasEnteredRef.current = true;
    if (entryTimeoutRef.current !== null) {
      window.clearTimeout(entryTimeoutRef.current);
      entryTimeoutRef.current = null;
    }
    onEntered?.();
    contentRef.current?.focus({ preventScroll: true });
  }, [onEntered]);
  const finishRestore = () => {
    if (!isRestoringRef.current) return;
    isRestoringRef.current = false;
    if (minimizeTimeoutRef.current !== null) {
      window.clearTimeout(minimizeTimeoutRef.current);
      minimizeTimeoutRef.current = null;
    }
    setIsFullscreenContent(false);
    onMaximizedChange?.(false);
  };
  const setMaximized = (maximized: boolean) => {
    if (minimizeTimeoutRef.current !== null) {
      window.clearTimeout(minimizeTimeoutRef.current);
      minimizeTimeoutRef.current = null;
    }

    setIsMaximized(maximized);

    if (maximized) {
      isRestoringRef.current = false;
      setIsFullscreenContent(true);
      onMaximizedChange?.(true);
      return;
    }

    isRestoringRef.current = true;
    if (prefersReducedMotion()) {
      finishRestore();
      return;
    }
    minimizeTimeoutRef.current = window.setTimeout(finishRestore, DRAWER_TRANSITION_FALLBACK_MS);
  };

  const finishResizing = () => {
    dragStartRef.current = null;
    setIsResizing(false);
    onResizingChange(false);
  };

  const onResizePointerDown: PointerEventHandler<HTMLButtonElement> = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dragStartRef.current = { pointerX: event.clientX, width };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setIsResizing(true);
    onResizingChange(true);
  };

  const onResizePointerMove: PointerEventHandler<HTMLButtonElement> = (event) => {
    const dragStart = dragStartRef.current;
    if (!dragStart) return;

    onWidthChange(clampDrawerWidth(dragStart.width + dragStart.pointerX - event.clientX));
  };

  const onResizePointerUp: PointerEventHandler<HTMLButtonElement> = (event) => {
    if (!dragStartRef.current) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    finishResizing();
  };

  const onResizeKeyDown: KeyboardEventHandler<HTMLButtonElement> = (event) => {
    let nextWidth: number | undefined;

    // The exposed value is the width, so it has to follow the ARIA convention
    // rather than the drag direction: Right/Up raise it even though the drawer's
    // edge travels left as it widens.
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      nextWidth = width + KEYBOARD_RESIZE_STEP;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      nextWidth = width - KEYBOARD_RESIZE_STEP;
    }
    if (event.key === 'Home') nextWidth = MIN_DRAWER_WIDTH;
    if (event.key === 'End') nextWidth = getMaximumDrawerWidth();
    if (nextWidth === undefined) return;

    event.preventDefault();
    onWidthChange(clampDrawerWidth(nextWidth));
  };

  // Read the width through a ref so the listener is registered once. With
  // `width` as a dependency this tore down and re-added a DOM listener on every
  // pointermove of a resize drag.
  const widthRef = useRef(width);

  useEffect(() => {
    widthRef.current = width;
  }, [width]);

  useEffect(() => {
    const clampWidthToViewport = () => {
      const clampedWidth = clampDrawerWidth(widthRef.current);
      if (clampedWidth !== widthRef.current) onWidthChange(clampedWidth);
    };

    window.addEventListener('resize', clampWidthToViewport);
    return () => window.removeEventListener('resize', clampWidthToViewport);
  }, [onWidthChange]);

  useEffect(() => {
    if (prefersReducedMotion()) {
      finishEntering();
      return;
    }

    entryTimeoutRef.current = window.setTimeout(finishEntering, DRAWER_TRANSITION_FALLBACK_MS);

    return () => {
      if (entryTimeoutRef.current !== null) window.clearTimeout(entryTimeoutRef.current);
    };
  }, [finishEntering]);

  useEffect(
    () => () => {
      if (minimizeTimeoutRef.current !== null) {
        window.clearTimeout(minimizeTimeoutRef.current);
      }
      if (entryTimeoutRef.current !== null) window.clearTimeout(entryTimeoutRef.current);
      onResizingChange(false);
    },
    [onResizingChange],
  );

  return (
    <Drawer.Root
      open
      modal={false}
      trapFocus={false}
      preventScroll={false}
      closeOnInteractOutside={false}
      closeOnEscape
      swipeDirection="end"
      onOpenChange={({ open }) => {
        if (!open) onClose();
      }}
    >
      <Portal>
        <Drawer.Positioner className="pointer-events-none fixed inset-0 z-70">
          <Drawer.Content
            ref={contentRef}
            tabIndex={-1}
            className={clsx(
              'agent-chat-drawer-enter ds-body-font pointer-events-auto fixed inset-y-0 right-0 flex max-w-full flex-col overflow-hidden overscroll-contain border-x border-b border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] transition-[width] ease-in-out will-change-[width] focus:outline-none motion-reduce:transition-none motion-reduce:will-change-auto',
              isResizing ? 'duration-0' : 'duration-300',
            )}
            style={{ width: isMaximized ? '100%' : `${width}px` }}
            id="agent-chat-drawer"
            onAnimationEnd={(event) => {
              if (
                event.currentTarget === event.target &&
                event.animationName === 'agent-chat-drawer-enter'
              ) {
                finishEntering();
              }
            }}
            onTransitionEnd={(event) => {
              if (event.currentTarget === event.target && event.propertyName === 'width') {
                finishRestore();
              }
            }}
          >
            {!isFullscreenContent && (
              <button
                type="button"
                role="slider"
                aria-label={t('prop-chat.resize')}
                aria-orientation="horizontal"
                aria-valuemin={MIN_DRAWER_WIDTH}
                aria-valuemax={getMaximumDrawerWidth()}
                aria-valuenow={width}
                aria-valuetext={t('prop-chat.resize-value', { width })}
                className="absolute inset-y-0 left-0 z-10 hidden w-[var(--ds-space-2)] cursor-col-resize touch-none border-0 bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[var(--ds-color-border-secondary)] md:block"
                onKeyDown={onResizeKeyDown}
                onPointerCancel={finishResizing}
                onPointerDown={onResizePointerDown}
                onPointerMove={onResizePointerMove}
                onPointerUp={onResizePointerUp}
              />
            )}
            <header className="flex h-[var(--ds-space-14)] shrink-0 items-center justify-between border-b border-solid border-[var(--ds-color-border-primary)] bg-[var(--ds-color-bg-primary)] px-[var(--ds-space-4)] pb-[var(--ds-space-0-25)]">
              <div className="flex min-w-0 flex-1 items-center gap-[var(--ds-space-2)]">
                <div
                  className={clsx(
                    'shrink-0 overflow-hidden transition-[width,opacity] duration-300 ease-in-out motion-reduce:transition-none',
                    isMaximized
                      ? 'w-[var(--ds-space-6)] opacity-100'
                      : 'pointer-events-none w-0 opacity-0',
                  )}
                >
                  <Button
                    aria-label={t('prop-chat.back')}
                    aria-hidden={!isMaximized}
                    disabled={!isMaximized}
                    title={t('prop-chat.back')}
                    tabIndex={isMaximized ? 0 : -1}
                    variant="ghost"
                    size="mini"
                    className="size-[var(--ds-space-6)] min-h-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1)]"
                    onClick={() => setMaximized(false)}
                  >
                    <ChevronLeft aria-hidden="true" className="size-[var(--ds-space-4)]" />
                  </Button>
                </div>
                <div className="flex items-center gap-[var(--ds-space-2)]">
                  <AgentChatLogo className="size-[var(--ds-space-6)]" />
                  <Drawer.Title asChild>
                    <h2 className="whitespace-nowrap font-[var(--ds-typography-font-definitions-font-family-body)] text-[length:var(--ds-typography-paragraph-13-regular-font-size)] font-[var(--ds-typography-paragraph-paragraph-medium-weight)] leading-[var(--ds-typography-paragraph-mini-line-height)] tracking-[var(--ds-typography-paragraph-mini-letter-spacing)] text-[var(--ds-color-text-primary)]">
                      {t('prop-chat.drawer-title')}
                    </h2>
                  </Drawer.Title>
                  <StatusBadge status="positive" label={t('prop-chat.beta')} showIcon={false} />
                </div>
              </div>
              <div className="flex items-center gap-[var(--ds-space-0-5)]">
                <Button
                  aria-label={resizeLabel}
                  title={resizeLabel}
                  aria-pressed={isFullscreenContent}
                  variant="ghost"
                  size="mini"
                  className="size-[var(--ds-space-6)] min-h-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1)]"
                  onClick={() => setMaximized(!isMaximized)}
                >
                  {isFullscreenContent ? (
                    <Minimize2 aria-hidden="true" className="size-[var(--ds-space-4)]" />
                  ) : (
                    <Maximize2 aria-hidden="true" className="size-[var(--ds-space-4)]" />
                  )}
                </Button>
                <Drawer.CloseTrigger asChild>
                  <Button
                    aria-label={t('prop-chat.close')}
                    title={t('prop-chat.close')}
                    variant="ghost"
                    size="mini"
                    className="size-[var(--ds-space-6)] min-h-0 rounded-[var(--ds-radius-4)] p-[var(--ds-space-1)]"
                  >
                    <X aria-hidden="true" className="size-[var(--ds-space-4)]" />
                  </Button>
                </Drawer.CloseTrigger>
              </div>
            </header>
            <div className="min-h-0 flex-1">
              <PropChatContent variant={isFullscreenContent ? 'fullscreen' : 'drawer'} />
            </div>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};
